import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ResourceBaseUrlBuilder } from './api/resource-base-url-builder';
import {
  getClientDdqRouteProductUidFromUrl,
  isClientDdqProductUrlPath,
  isClientInvestorDashboardShellPath,
  resolveClientDdqEmittentApiScopeFromSummaries,
} from './context/client-ddq-emittent-api-scope.util';
import { ClientDdqScopeCacheService } from './context/client-ddq-scope-cache.service';
import { OrganizationContextService } from './context/organization-context.service';
import { OrganizationQuery } from './organization/organization.query';
import { ProductSummaryQuery } from './product-summary/product-summary.query';
import { standardizeUrl } from './utils';

/**
 * Builds the following backend URLs:
 *   - emittents/{emittent_uid}
 *   - investors/{investor_uid}
 *
 * When in fund-ddq-runs context:
 *   - Defaults to 'emittent' type for service calls
 *   - Uses issuerUid from context instead of organization.id
 *   - Ensures API compatibility while allowing investor navigation display
 */
@Injectable({ providedIn: 'root' })
export class UserNamespacedResourceBaseUrlBuilder implements ResourceBaseUrlBuilder {
  constructor(
    private organizationQuery: OrganizationQuery,
    private organizationContextService: OrganizationContextService,
    private router: Router,
    private productSummaryQuery: ProductSummaryQuery,
    private clientDdqScopeCacheService: ClientDdqScopeCacheService,
  ) {}

  public buildBaseUrl(append?: string): string {
    const organization = this.organizationQuery.getActive();
    const contextValue = this.organizationContextService.getContextValue();
    const pathOnly = (this.router.url ?? '').split('?')[0];

    // Client marketplace selling eligibility is always under the investor namespace.
    if (
      append?.match(/^clients\/[^/]+\/eligibility$/) &&
      organization?.type === 'investor' &&
      organization.id
    ) {
      return standardizeUrl(`investors/${organization.id}/${append}`);
    }

    // Marketplace / baskets / DDQ invites list: always investor namespace for this client shell.
    if (isClientInvestorDashboardShellPath(pathOnly) && organization?.type === 'investor' && organization.id) {
      let url = `investors/${organization.id}`;
      if (append) {
        url += `/${append}`;
      }
      return standardizeUrl(url);
    }

    // When in fund-ddq-runs context, use issuerUid instead of organization.id
    if (contextValue?.context === 'fund-ddq-runs' && contextValue?.issuerUid) {
      const effectiveType = contextValue.serviceType || 'emittent';
      let url = `${effectiveType}s/${contextValue.issuerUid}`;
      if (append) {
        url += `/${append}`;
      }
      return standardizeUrl(url);
    }

    const clientDdqScope = resolveClientDdqEmittentApiScopeFromSummaries(
      organization?.type,
      isClientDdqProductUrlPath(this.router.url) ? this.productSummaryQuery.getAll() : [],
    );
    if (clientDdqScope) {
      let url = `emittents/${clientDdqScope.issuerUid}`;
      if (append) {
        url += `/${append}`;
      }
      return standardizeUrl(url);
    }
    if (isClientDdqProductUrlPath(this.router.url)) {
      const routeProductUid = getClientDdqRouteProductUidFromUrl(this.router.url);
      const cachedScope = this.clientDdqScopeCacheService.getSnapshotForRouteProduct(routeProductUid);
      if (cachedScope) {
        let url = `emittents/${cachedScope.issuerUid}`;
        if (append) {
          url += `/${append}`;
        }
        return standardizeUrl(url);
      }
    }

    // Fallback to normal behavior: use organization from store
    if (!organization) {
      // Return empty string to maintain backward compatibility
      // This will cause API calls to fail gracefully rather than throwing immediately
      return '';
    }

    // Get effective organization type from context service
    // This allows overriding to 'emittent' when in fund-ddq-runs context
    // while maintaining backward compatibility
    let effectiveType = organization.type;
    if (contextValue?.serviceType) {
      effectiveType = contextValue.serviceType;
    }

    let url = `${effectiveType}s/${organization.id}`;
    if (append) {
      url += `/${append}`;
    }
    return standardizeUrl(url);
  }
}
